import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { splitInclusiveTotal, grossOnScheduledRowBoundary } from 'src/app/services/studio-tax.service';
import { FormGroup, Validators, FormArray, NgForm, AbstractControl } from '@angular/forms';
import _ from 'lodash';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { CreditCardReadInputComponent } from '../credit-card-read-input/credit-card-read-input.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SquareWebPaymentComponent } from '../square-web-payment/square-web-payment.component';
import { SquareTerminalPaymentComponent } from '../square-terminal-payment/square-terminal-payment.component';
import { PaybrightTerminalPaymentComponent } from '../paybright-terminal-payment/paybright-terminal-payment.component';
import { v4 as uuidv4 } from 'uuid';
import { SquareTerminalSaveCardComponent } from '../square-terminal-save-card-component/square-terminal-save-card-component.component';
import { PaymentMethodsDialogComponent } from '../../configuration/payment-configuration/payment-methods-dialog/payment-methods-dialog.component';
import { TAX_TYPES } from '../../../constants/tax.constants';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-payments-entity-form',
  templateUrl: './payments-entity-form.component.html',
  styleUrls: ['./payments-entity-form.component.css']
})
export class PaymentsEntityFormComponent extends BaseDialogComponent implements OnInit, OnDestroy {

  // @ViewChild(SquareWebPaymentComponent) squareWebPayment: SquareWebPaymentComponent;

  override eckType = "payments";
  override bundle = "payment";
  override entityId = this._injectedDialogData['data']['EntityID'];

  taxTypes = TAX_TYPES;

  private _studentData = new BehaviorSubject<any>(null);
  _enrollmentData = new BehaviorSubject<any>(null);

  @Input() isNested: boolean = false;
  @Input() set studentData(value: any) {
    this._studentData.next(value);
  }
  @Input() set enrollmentData(value: any) {
    this._enrollmentData.next(value);
  }

  @Output() paymentSubmitted = new EventEmitter<any>();

  CreditCardReadInputComponent = CreditCardReadInputComponent;
  SquareTerminalPaymentComponent = SquareTerminalPaymentComponent;

  paymentDeleteForm: FormGroup;
  field_enrollment_name: any;
  __field_enrollment_name: any;
  isSubmitting: boolean = false;

  // Stripe idempotency key for the payment being submitted. Kept for as long
  // as the server has not answered, so a resubmit after a timeout or dropped
  // connection replays the same charge instead of making a new one. Cleared
  // once the server responds (success or error), because a failed attempt is
  // rolled back and refunded server-side and must not be replayed.
  private paymentIdempotencyKey: string | null = null;

  vatTaxRate = 0;
  gstTaxRate = 0;
  salesTaxRate = 0;

  instructorPercentageEditMode: boolean = false;
  enrollmentPaymentError: string = '';

  taxBreakdownSummary: string = '';
  // Raw studio tax config rows (create mode) so the breakdown can be
  // re-resolved when the payment date moves into a different rate era.
  private taxConfigRows: any[] | null = null;
  isUpdatingTaxes = false;
  showTaxBreakdown: boolean = false; // To toggle visibility of tax breakdown

  // Properties for receipt tab
  public paymentSuccess = false;
  public receiptData: any = null;

  // Fiscal Code (Codice Fiscale) is collected when either the studio or the
  // selected student is in Italy/Spain. Tracked separately so a student
  // selection can light it up even at a non-IT/ES studio.
  public showFiscalCode = false;
  private studioIsFiscalCountry = false;

  toggleTaxBreakdown() {
    this.showTaxBreakdown = !this.showTaxBreakdown;
  }

  // Studio config is app-lifetime; released so each closed payment dialog can be collected.
  private countryCodeSubscription: Subscription;

  override ngOnDestroy(): void {
    this.countryCodeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  override ngOnInit(): void {
    // When used as nested component in enrollment flow, default to create mode
    if (this.isNested) {
      this.initActionType = 'create';
      this.actionType = 'create';
      this.selectedIndex = 1; // Switch to Create tab
    } else {
      this.initActionType = this._injectedDialogData['data']['action'];
      this.actionType = this._injectedDialogData['data']['action'];
    }

    let formGroup = {
      field_payment_id: [this.paymentID, Validators.required],
      field_date_and_time: [this._enrollmentData?.['field_date_and_time'] || '', Validators.required], // [this.todaysDate, Validators.required],
      field_gross_tuition: [null, Validators.required],
      field_student_name: [null, Validators.required],
      field_scheduled_payment: this._formBuilder.group({
        'target_id': [null]
      }),
      field_payment_type: [null, Validators.required],
      cc_number: [null],
      cc_cvv: [null],
      cc_expiration: [null],
      cc_expiration_month: [null],
      cc_expiration_year: [null],
      cc_postal_code: [null],
      field_stripe_charge_id: [this.entityData?.field_stripe_charge_id],
      reason_to_refund: [null],
      field_payments_use_card: [null],
      field_check_number: [null],
      field_tax_rate_at_time_of_pay: [null],
      field_total_payment_amount: [null],
      // Create and refund both compute tax from a live breakdown; edit shows
      // the stored one read-only.
      field_tax_breakdown: (this.initActionType === 'create' || this.initActionType === 'refund') ? this._formBuilder.array([]) : this._formBuilder.control(''),
      field_tax_collected: [null],
      field_sales_tax_amount: [null],
      field_notes: [null],
      field_fiscal_code: [null, Validators.maxLength(16)],
    }

    let formGroupEdit;

    if (this.initActionType == "create") {
      formGroupEdit = {
        ...formGroup,
        field_enrollment_name: new FormArray([
          this._formBuilder.group({
            'target_id': [null, Validators.required],
            'payment_amount': [0, Validators.required],
          })
        ]),
      }
      this.loadTaxRatesFromConfig();
    } else {
      formGroupEdit = {
        ...formGroup,
        field_enrollment_name: this._formBuilder.group({
          'target_id': [null, Validators.required]
        }),
        field_tax_collected: [null], // Add field_tax_collected here for non-create states
      }
      this.loadTaxRatesFromEntity();
    }

    this.paymentForm = this._formBuilder.group(formGroupEdit);

    // Show the Fiscal Code when the studio is in Italy/Spain. A student
    // selection can additionally enable it (see onStudentNameSelectThenLoadFiscalCode).
    this.countryCodeSubscription = this._studioConfigService.getCountryCode().subscribe(countryCode => {
      this.studioIsFiscalCountry = ['IT', 'ES'].includes(countryCode);
      this.showFiscalCode = this.showFiscalCode || this.studioIsFiscalCountry;
    });

    // Subscribe to changes in field_gross_tuition
    this.paymentForm.get('field_gross_tuition').valueChanges.subscribe(value => {
      this.updateTotalPaymentAmount();
      this.updateTaxesAndTotal();
    });

    // Subscribe to changes in field_tax_rate_at_time_of_pay
    this.paymentForm.get('field_tax_rate_at_time_of_pay').valueChanges.subscribe(value => {
      this.updateTaxesAndTotal();
    });

    this.paymentDeleteForm = this._formBuilder.group({
      field_reason_to_delete: [null, Validators.required],
    });

    this.paymentForm.get('field_payment_type').valueChanges.subscribe(value => {
      if (value == 1407 /* Stripe */ && this.initActionType == 'create') {
        this.paymentForm.controls['field_payments_use_card'].setValidators([Validators.required]);
      } else {
        this.paymentForm.controls['field_payments_use_card'].clearValidators();
      }
      this.paymentForm.controls['field_payments_use_card'].updateValueAndValidity();

      // When PayBright is chosen, load the student's saved cards so they appear
      // in the selection list (the student may have been picked beforehand).
      if (this.isPaybrightTerminalSelected) {
        this.loadPaybrightSavedCards();
      }
      // Likewise the saved bank accounts for PayBright ACH. Reset the new
      // account entry so numbers never linger across method changes.
      if (this.isPaybrightAchSelected) {
        this.loadPaybrightBankAccounts();
      } else {
        this.resetAchEntry();
      }
    });

    if (this.initActionType === 'create') {
      this.paymentForm.get('field_gross_tuition').valueChanges.subscribe(value => {
        this.distributeGrossTuitionEqually(value);
      });

      this.paymentForm.get('field_gross_tuition').valueChanges.subscribe(() => {
        setTimeout(() => this.checkEnrollmentPayments(), 0);
      });

      this.paymentForm.get('field_enrollment_name').valueChanges.subscribe(() => {
        setTimeout(() => this.checkEnrollmentPayments(), 0);
      });

      if (Array.isArray(this.paymentForm.get('field_enrollment_name').value)) {
        this.paymentForm.get('field_enrollment_name').valueChanges.subscribe(() => {
          const enrollmentFormArray = this.paymentForm.get('field_enrollment_name') as FormArray;
          enrollmentFormArray.controls.forEach((group: FormGroup, index: number) => {
            if (group.dirty) {
              this.enrollmentTouched = index;
            }
          });
        });
      }

      this.checkEnrollmentPayments();
    }

    if (this.initActionType == 'create') {
      this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').subscribe((response: any[]) => {
        this.taxConfigRows = Array.isArray(response) ? response : (response ? [response] : null);
        this.setTaxBreakdown(response, true);
      });

      // Tax rates are date-effective and the payment date is backdatable
      // (history entry, nested form seeding the enrollment's sale date): the
      // breakdown must follow the payment's date, not the day it's typed in.
      this.paymentForm.get('field_date_and_time')?.valueChanges.subscribe(() => {
        if (this.taxConfigRows) {
          this.setTaxBreakdown(this.taxConfigRows, false);
        }
      });
    } else if (this.entityData && this.entityData.field_tax_breakdown) {
      this.setTaxBreakdown(this.entityData.field_tax_breakdown, true);
    }


    if (this.isNested) {
      this._studentData.subscribe(student => {
        if (student) {
          const studentName = `${student.title} (${student.id})`;
          this.paymentForm.patchValue({
            field_student_name: studentName
          });
          this.onStudentNameSelectThenLoadEnrollmentName(student.id, false, null, true);
        }
      });

      this._enrollmentData.subscribe(enrollment => {
        if (enrollment) {
          LoggerService.log('Enrollment data:', enrollment);

          // Update the payment date using the enrollment's sale date
          if (enrollment.field_sale_date) {
            this.paymentForm.patchValue({
              field_date_and_time: enrollment.field_sale_date
            });
          }
          // this.paymentForm.patchValue({
          //   field_enrollment_name: {
          //     target_id: enrollment.id
          //   }
          // });

          const fieldEnrollmentName = this.paymentForm.get('field_enrollment_name') as FormArray;

          // Clear the existing array
          fieldEnrollmentName.clear();

          // Add the new enrollment
          fieldEnrollmentName.push(this._formBuilder.group({
            target_id: enrollment.id,
            payment_amount: 0
          }));
        }
      });
    }

    this.paymentForm.get('field_payments_use_card')?.valueChanges.subscribe(value => {
      if (value === 'add_new_card' && this.paymentForm.get('field_payment_type')?.value === '1422') {
        // Reset the value to prevent immediate re-triggering
        this.paymentForm.get('field_payments_use_card')?.setValue(null, { emitEvent: false });
        // Open the save card dialog
        this.openSquareTerminalSaveCard();
      }

      // Picking a saved PayBright card makes the save-card option irrelevant
      // (it's only for taking a fresh card on the terminal); clear any stale intent.
      if (value && value !== 'terminal_payment') {
        this.paybrightSaveCard = false;
      }
    });
  }


  // When a student is selected, enable the Fiscal Code field if the student's
  // address is in Italy/Spain (in addition to the studio check), and pre-fill any
  // code already saved on their primary contact so it carries to later payments.
  onStudentNameSelectThenLoadFiscalCode($studentID: any) {
    let studentID = ($studentID?.option?.value ? $studentID.option.value : $studentID);
    // Extract the student ID from parenthesis, e.g. "John Doe (1234)".
    let regExp = /\(([^)]+)\)(?!.*\()/;
    let matches = regExp.exec(studentID);
    if (!studentID || !matches) { return; }

    this._entityRESTService.getEntity('student_accounts', 'student_account', matches[1])
      .subscribe((student: any) => {
        const contacts = student?.field_contacts;
        const contact = Array.isArray(contacts) ? contacts[0] : contacts;

        // Enable the field if the studio OR the student is in Italy/Spain.
        const studentCountry = contact?.field_address?.country_code;
        const studentIsFiscalCountry = ['IT', 'ES'].includes(studentCountry);
        this.showFiscalCode = this.studioIsFiscalCountry || studentIsFiscalCountry;

        const fiscalCode = contact?.field_fiscal_code;
        if (fiscalCode) {
          this.paymentForm.get('field_fiscal_code')?.setValue(fiscalCode);
        }
      });
  }

  override getEntity(eckType: string, bundle: string, EntityID: number, formRef?) {
    this.displayProgressSpinner(true);

    this._entityRESTService.getEntity(eckType, bundle, EntityID)
      .subscribe((data: any) => {
        // If initActionType is refund, parse field_stripe_charge_object as json. TODO: this should be differently.
        if (this.initActionType == 'refund') {
          try {
            data.field_stripe_charge_object = JSON.parse(data?.['field_stripe_charge_object']);
          } catch (e) {
            // Do nothing.
          }
        }
        if (this.initActionType == 'edit') {
          data.field_tax_breakdown = data.field_tax_breakdown || '[]'; // Ensure it's initialized as a string
          LoggerService.log('data.field_tax_breakdown:', data.field_tax_breakdown);

          // Create a summary of the tax breakdown
          this.taxBreakdownSummary = this.createTaxBreakdownSummary(data.field_tax_breakdown);
        }

        this.entityData = data;

        // Progress spinner.
        this.displayProgressSpinner(false);

        // Update form values
        this.setFormValues(formRef);
      });
  }

  createTaxBreakdownSummary(taxBreakdown: string): string {
    let parsedTaxBreakdown: any[] = [];
    try {
      parsedTaxBreakdown = JSON.parse(taxBreakdown);
    } catch (e) {
      LoggerService.error('Error parsing tax breakdown JSON:', e);
    }

    if (!Array.isArray(parsedTaxBreakdown)) {
      return '';
    }

    return parsedTaxBreakdown.map(tax => {
      return `${tax.field_tax_type}: ${parseFloat(tax.field_tax_value) || 0}${tax.field_calculation_method === 'PERCENTAGE' ? '%' : ''}`;
    }).join(', ');
  }

  setTaxRates(response: any) {
    this.salesTaxRate = Number(response?.['field_sales_tax_rate']?.[0]?.['value']) || 0;
    this.gstTaxRate = Number(response?.['field_gst_tax_rate']?.[0]?.['value']) || 0;
    this.vatTaxRate = Number(response?.['field_vat_tax_rate']?.[0]?.['value']) || 0;
  }

  private handleNoTaxConfig(isEditMode: boolean) {
    LoggerService.log('Handling no tax configuration');
    const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;
    taxBreakdownArray.clear();

    if (!isEditMode) {
      this.paymentForm.patchValue({
        field_tax_rate_at_time_of_pay: 0,
        field_tax_collected: 0
      });
      this.updateTaxesAndTotal();
    }
  }

  /**
   * A refund reverses tax at the rates the original payment was taxed under,
   * so its breakdown comes from that payment's stored rows (the rate era it
   * was made in, its rounding rules and inclusive flags) rather than today's
   * config. A legacy original with no stored rows falls back to the studio
   * config resolved as of the original's payment date. The current Refund
   * Amount (defaulted to the original's amount) is then re-taxed.
   */
  private setRefundTaxBreakdownFromOriginal(storedBreakdown: any, originalDate: any): void {
    let rows: any[] = [];
    try {
      rows = typeof storedBreakdown === 'string' ? JSON.parse(storedBreakdown) : storedBreakdown;
    } catch (e) {
      rows = [];
    }
    const usable = Array.isArray(rows) ? rows.filter(row => row && parseFloat(row.field_tax_value) > 0 && row.field_calculation_method) : [];
    if (usable.length) {
      // Stored rows are flat; setTaxBreakdown reads config-shaped [{value}] fields.
      const asConfig = usable.map(row => ({
        field_tax_status: [{ value: '1' }],
        field_tax_type: [{ value: row.field_tax_type }],
        field_tax_value: [{ value: String(row.field_tax_value) }],
        field_calculation_method: [{ value: row.field_calculation_method }],
        field_rounding_rule: [{ value: row.field_rounding_rule || 'ROUND_NEAREST' }],
        field_is_tax_inclusive: [{ value: row.field_is_tax_inclusive ?? '0' }],
      }));
      this.setTaxBreakdown(asConfig, false);
      return;
    }
    const asOf = originalDate && moment(originalDate).isValid() ? moment(originalDate) : moment();
    this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').subscribe(
      (response: any) => {
        const config = Array.isArray(response) ? response : (response ? [response] : []);
        this.setTaxBreakdown(config, false, asOf);
      },
      () => this.handleNoTaxConfig(false)
    );
  }

  override setFormValues(formRef = null) {
    if (this.initActionType !== 'refund' || !this.entityData) {
      super.setFormValues(formRef);
      return;
    }
    // The original's breakdown is a JSON string; in refund mode the control is
    // a FormArray, so keep the string out of the generic patch and build the
    // rows from it afterwards.
    const storedBreakdown = this.entityData.field_tax_breakdown;
    delete this.entityData.field_tax_breakdown;
    super.setFormValues(formRef);
    this.entityData.field_tax_breakdown = storedBreakdown;
    this.setRefundTaxBreakdownFromOriginal(storedBreakdown, this.entityData.field_date_and_time);
  }

  setTaxBreakdown(taxConfig: any[] | null, isEditMode: boolean, asOf: moment.Moment | null = null) {
    LoggerService.log('Setting tax breakdown:', taxConfig);

    const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;
    if (!taxBreakdownArray) {
      LoggerService.error('field_tax_breakdown FormArray is not initialized');
      return;
    }
    taxBreakdownArray.clear();

    if (!taxConfig || !Array.isArray(taxConfig) || taxConfig.length === 0) {
      LoggerService.warn('Invalid or empty tax configuration received');
      this.handleNoTaxConfig(isEditMode);
      return;
    }

    const currentDate = asOf ?? this.paymentDateForTaxes();

    let added = this.pushEffectiveTaxRows(taxConfig, currentDate, taxBreakdownArray);

    // No row covers the payment's date (e.g. it predates every configured
    // era): fall back to today's rates rather than silently taxing at 0%.
    if (added === 0 && !currentDate.isSame(moment(), 'day')) {
      added = this.pushEffectiveTaxRows(taxConfig, moment(), taxBreakdownArray);
    }

    this.updateFormValuesAfterTaxBreakdown(isEditMode);
  }

  private pushEffectiveTaxRows(taxConfig: any[], asOf: moment.Moment, taxBreakdownArray: FormArray): number {
    let added = 0;

    taxConfig.forEach(tax => {
      if (!tax || typeof tax !== 'object') {
        LoggerService.warn('Invalid tax object in configuration, skipping');
        return;
      }

      // Check if tax is active
      const taxStatus = this.getTaxValue(tax, 'field_tax_status');
      const isActive = taxStatus === '1' || taxStatus === 'true';
      if (!isActive) {
        LoggerService.log('Skipping inactive tax configuration:', tax);
        return;
      }

      const startDate = this.getTaxDate(tax, 'field_effective_tax_start_date');
      const endDate = this.getTaxDate(tax, 'field_effective_tax_end_date');

      if (this.isTaxEffective(startDate, endDate, asOf)) {
        const taxGroup = this.createTaxGroup(tax);
        taxBreakdownArray.push(taxGroup);
        added++;

        // Subscribe to changes in this tax group
        taxGroup.valueChanges.subscribe(() => {
          this.updateTaxesAndTotal();
        });
      }
    });

    return added;
  }

  private getTaxDate(tax: any, fieldName: string): moment.Moment | null {
    const dateValue = tax[fieldName]?.[0]?.value;
    return dateValue ? moment(dateValue) : null;
  }

  /**
   * The date tax rates should be resolved against: the payment's own date when
   * set (rate eras are legally determined by when a payment is made/becomes
   * due, so a backdated history payment must use its era's rate), else today.
   */
  private paymentDateForTaxes(): moment.Moment {
    const value = this.paymentForm?.get('field_date_and_time')?.value;
    return value && moment(value).isValid() ? moment(value) : moment();
  }

  private isTaxEffective(startDate: moment.Moment | null, endDate: moment.Moment | null, currentDate: moment.Moment): boolean {
    // Day granularity: a payment at 2pm on a rate's last effective day still
    // belongs to that rate era even though the config stores midnight.
    return (!startDate || currentDate.isSameOrAfter(startDate, 'day')) && (!endDate || currentDate.isSameOrBefore(endDate, 'day'));
  }

  private createTaxGroup(tax: any) {
    return this._formBuilder.group({
      field_tax_type: [this.getTaxValue(tax, 'field_tax_type'), Validators.required],
      field_tax_value: [this.getTaxValue(tax, 'field_tax_value'), Validators.required],
      field_calculation_method: [this.getTaxValue(tax, 'field_calculation_method'), Validators.required],
      field_rounding_rule: [this.getTaxValue(tax, 'field_rounding_rule'), Validators.required],
      field_is_tax_inclusive: [this.getTaxValue(tax, 'field_is_tax_inclusive')],
      // Computed dollar amount for this tax, written by updateTaxesAndTotal().
      // Stored in field_tax_breakdown so per-tax collected amounts can be
      // reported later. The rate alone can't be turned back into an amount
      // once a payment is prorated across enrollments.
      field_tax_amount: [null],
    });
  }

  private updateFormValuesAfterTaxBreakdown(isEditMode: boolean) {
    if (isEditMode && this.entityData) {
      // Preserve existing tax-related values in edit mode
      this.paymentForm.patchValue({
        field_tax_rate_at_time_of_pay: this.displayRate(this.entityData.field_tax_rate_at_time_of_pay),
        field_tax_collected: this.entityData.field_tax_collected,
        field_total_payment_amount: this.entityData.field_total_payment_amount
      });
    } else {
      this.updateTaxesAndTotal();
    }
  }

  /** Decimal fields come back at storage scale ("10.000"); show the number. */
  private displayRate(value: any): any {
    const num = parseFloat(value);
    return isNaN(num) ? value : num;
  }

  private getTaxValue(tax: any, field: string): string | null {
    return tax[field]?.[0]?.value || null;
  }

  /**
   * Sum of the exclusive PERCENTAGE tax rates in the current breakdown (the part
   * added ON TOP of gross tuition). 0 for inclusive-tax and untaxed studios.
   */
  get exclusiveTaxRate(): number {
    const rows = (this.paymentForm?.get('field_tax_breakdown') as FormArray)?.controls ?? [];
    return rows.reduce((sum, ctrl) => {
      const tax = ctrl.value || {};
      const inclusive = tax.field_is_tax_inclusive === '1' || tax.field_is_tax_inclusive === 'true' || tax.field_is_tax_inclusive === true;
      if (inclusive || tax.field_calculation_method !== 'PERCENTAGE') return sum;
      return sum + (parseFloat(tax.field_tax_value) || 0);
    }, 0);
  }

  /** Flat-amount exclusive taxes in the breakdown (rare; subtracted before back-calculating gross). */
  private get exclusiveFlatTax(): number {
    const rows = (this.paymentForm?.get('field_tax_breakdown') as FormArray)?.controls ?? [];
    return rows.reduce((sum, ctrl) => {
      const tax = ctrl.value || {};
      const inclusive = tax.field_is_tax_inclusive === '1' || tax.field_is_tax_inclusive === 'true' || tax.field_is_tax_inclusive === true;
      if (inclusive || tax.field_calculation_method !== 'FLAT_RATE') return sum;
      return sum + (parseFloat(tax.field_tax_value) || 0);
    }, 0);
  }

  /** True when the studio adds tax on top of tuition (Canadian GST/HST/PST). */
  get hasExclusiveTax(): boolean {
    return this.exclusiveTaxRate > 0 || this.exclusiveFlatTax > 0;
  }

  // The tax-inclusive Total as the staff typed it. While set, the recompute
  // keeps this figure exactly and absorbs the rounding remainder into the
  // collected tax (the standard tax-included split) instead of overwriting a
  // round figure like $150.00 with the re-rounded $149.99.
  private lockedTotalFromEntry: number | null = null;

  // The typed Total paid one or more scheduled rows to the cent and the
  // pre-tax amount was taken from the schedule rather than re-divided. The
  // forward tax can then sit a few cents off the typed Total (up to ten);
  // the lock absorbs that into the collected tax.
  private grossSnappedToScheduledRow = false;

  /**
   * Staff typed the amount the customer is actually paying (tax-inclusive) in
   * the Total box: back-calculate the pre-tax Payment Amount from it. Setting
   * field_gross_tuition re-runs updateTaxesAndTotal(), which recomputes tax;
   * lockedTotalFromEntry keeps the Total exactly as typed.
   */
  onTotalEntered(): void {
    if (this.initActionType !== 'create' && this.initActionType !== 'refund') return;
    const total = parseFloat(String(this.paymentForm.get('field_total_payment_amount').value ?? '').replace(/[^0-9.\-]/g, ''));
    if (isNaN(total)) {
      this.lockedTotalFromEntry = null;
      this.grossSnappedToScheduledRow = false;
      return;
    }
    this.lockedTotalFromEntry = Math.round(total * 100) / 100;
    // The same shared split the enrollment form uses for scheduled rows and
    // the down payment. Dividing by the rate here while the rows used per-tax
    // extraction disagreed on the gross ~25% of the time at dual-tax studios
    // (GST + PST/QST), leaving 1¢ owing on a row after its exact Total was
    // paid. Flat exclusive taxes come off the top; only percentage rows split.
    const percentageRows = ((this.paymentForm.get('field_tax_breakdown') as FormArray)?.controls ?? [])
      .map(ctrl => ctrl.value || {})
      .filter(tax => {
        const inclusive = tax.field_is_tax_inclusive === '1' || tax.field_is_tax_inclusive === 'true' || tax.field_is_tax_inclusive === true;
        return !inclusive && tax.field_calculation_method === 'PERCENTAGE' && (parseFloat(tax.field_tax_value) || 0) > 0;
      })
      .map(tax => ({ rate: parseFloat(tax.field_tax_value), roundingRule: tax.field_rounding_rule || 'ROUND_NEAREST' }));
    const taxedTotal = Math.round((total - this.exclusiveFlatTax) * 100) / 100;
    const split = splitInclusiveTotal(taxedTotal, percentageRows);
    // A Total that pays the selected enrollment's scheduled row(s) to the cent
    // records the schedule's own pre-tax amount, so the cent the two splits
    // can disagree by never spills onto the next row.
    const onRow = this.grossOnScheduledRow(taxedTotal, split.gross);
    this.grossSnappedToScheduledRow = onRow !== null;
    this.paymentForm.get('field_gross_tuition').setValue((onRow ?? split.gross).toFixed(2));
  }

  /** Staff typed the pre-tax amount directly. The Total re-derives forward. */
  onGrossEdited(): void {
    this.lockedTotalFromEntry = null;
    this.grossSnappedToScheduledRow = false;
  }

  /**
   * The pre-tax amount the schedule itself holds for a typed Total that is
   * exactly what the one selected enrollment still owes through a scheduled
   * row, or null: several or no enrollments selected, no schedule or actuals
   * on the picker row, or a Total that is not a row boundary.
   */
  private grossOnScheduledRow(total: number, gross: number): number | null {
    if (this.initActionType !== 'create' || !this.hasExclusiveTax) return null;
    const rows = this.paymentForm.get('field_enrollment_name')?.value;
    const ids = (Array.isArray(rows) ? rows : []).map(row => row?.target_id).filter(id => !!id);
    if (ids.length !== 1) return null;
    const element = this.availableEnrollmentsForUser.find(e => e.id?.[0]?.value == ids[0]);
    const schedule = Array.isArray(element?.scheduled_rows) ? element.scheduled_rows : [];
    const paidGross = parseFloat(element?.paid_gross_actual);
    const paidTax = parseFloat(element?.paid_tax_actual);
    if (!schedule.length || isNaN(paidGross) || isNaN(paidTax)) return null;
    return grossOnScheduledRowBoundary(total, gross,
      schedule.map(r => ({ gross: parseFloat(r?.gross) || 0, tax: parseFloat(r?.tax) || 0 })),
      paidGross, paidTax);
  }

  /**
   * Tax-inclusive view of one enrollment's split amount, for the hint under
   * the "Amount for Enrollment" box. Display only. The splits themselves
   * store pre-tax gross, like everything else. Percentage taxes only: a flat
   * exclusive tax applies once to the payment, not per enrollment.
   */
  splitInclTax(index: number): number {
    const rows = this.paymentForm.get('field_enrollment_name') as FormArray;
    const amount = parseFloat(rows?.at(index)?.get('payment_amount')?.value) || 0;
    return Math.round(amount * (1 + this.exclusiveTaxRate / 100) * 100) / 100;
  }

  /**
   * Balance due shown in the enrollment picker. Enrollments store the pre-tax
   * balance; for exclusive-tax studios show what the customer actually owes by
   * adding the enrollment's own tax ratio (field_taxes / field_enrollment_total_price).
   */
  enrollmentDue(element: any): number {
    const due = parseFloat(element?.field_balance_due?.[0]?.value) || 0;
    return due + this.enrollmentDueTax(element, due);
  }

  enrollmentDueHasTax(element: any): boolean {
    return this.enrollmentDueTax(element, parseFloat(element?.field_balance_due?.[0]?.value) || 0) > 0;
  }

  private enrollmentDueTax(element: any, due: number): number {
    if (!this.hasExclusiveTax || !(due > 0)) return 0;
    const price = parseFloat(element?.field_enrollment_total_price?.[0]?.value) || 0;
    const tax = parseFloat(element?.field_taxes?.[0]?.value) || 0;
    if (!(price > 0) || !(tax > 0)) return 0;
    // Prefer what is really still owed: prorating the balance lands a cent
    // short once the estimate's half-cent rounding is gone ($4,237.72 at 13%
    // shows $4,788.62 owing when the enrollment's remaining tax is $550.91).
    const remaining = this.enrollmentRemainingTax(element);
    if (remaining !== null) return remaining;
    return Math.round(due * (tax / price) * 100) / 100;
  }

  /**
   * Tax the enrollment still owes: its estimated tax less what its payments
   * actually collected (paid_tax_actual, served with the picker rows by
   * enrollmentLookupDetailed). null when the row carries no actuals.
   */
  private enrollmentRemainingTax(element: any): number | null {
    const tax = parseFloat(element?.field_taxes?.[0]?.value) || 0;
    const paidTax = parseFloat(element?.paid_tax_actual);
    if (!(tax > 0) || element?.paid_gross_actual === undefined || isNaN(paidTax)) return null;
    return Math.max(0, Math.round((tax - paidTax) * 100) / 100);
  }

  /**
   * The tax still owed on the one enrollment this payment pays off, or null
   * when this isn't a single-enrollment closing payment: several or no
   * enrollments selected, no actuals on the row, or a pre-tax amount that
   * isn't the enrollment's balance to the cent.
   */
  private closingPaymentTax(gross: number): number | null {
    if (this.initActionType !== 'create') return null;
    const rows = this.paymentForm.get('field_enrollment_name')?.value;
    const ids = (Array.isArray(rows) ? rows : []).map(row => row?.target_id).filter(id => !!id);
    if (ids.length !== 1) return null;
    const element = this.availableEnrollmentsForUser.find(e => e.id?.[0]?.value == ids[0]);
    if (!element) return null;
    const due = parseFloat(element?.field_balance_due?.[0]?.value) || 0;
    if (!(due > 0) || Math.round(gross * 100) !== Math.round(due * 100)) return null;
    return this.enrollmentRemainingTax(element);
  }

  updateTaxesAndTotal() {
    if (this.isUpdatingTaxes || this.initActionType === 'edit') {
      return;
    }

    this.isUpdatingTaxes = true;

    try {
      const grossTuition = Number(this.paymentForm.get('field_gross_tuition').value) || 0;
      const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;

      let totalTaxAmount = 0;
      let inclusiveTaxAmount = 0;
      let exclusiveTaxAmount = 0;
      let effectiveTaxRate = 0;
      let largestExclusiveCtrl: AbstractControl | null = null;
      let largestExclusiveAmount = -1;

      if (taxBreakdownArray.length > 0) {
        // Stacked inclusive taxes must extract against the COMBINED inclusive
        // rate: each tax is r_i% of the pre-tax base = gross / (1 + R_incl).
        // Extracting each row independently counts the other tax's share
        // twice ($676.20 at GST 5 + QST 9.975 both-inclusive yielded $93.53
        // instead of the correct $88.07).
        const inclusiveRateSum = taxBreakdownArray.controls.reduce((sum, ctrl) => {
          const t = ctrl.value || {};
          const incl = t.field_is_tax_inclusive === '1' || t.field_is_tax_inclusive === 'true' || t.field_is_tax_inclusive === true;
          return sum + (incl && t.field_calculation_method === 'PERCENTAGE' ? (parseFloat(t.field_tax_value) || 0) : 0);
        }, 0);

        taxBreakdownArray.controls.forEach((taxControl: AbstractControl) => {
          const tax = taxControl.value;
          if (!tax.field_tax_value || !tax.field_calculation_method) {
            // Clear any previously computed amount so an abandoned row can't
            // carry a stale figure into the stored breakdown.
            taxControl.patchValue({ field_tax_amount: null }, { emitEvent: false });
            return; // Skip incomplete tax entries
          }

          let taxAmount = 0;
          const isInclusive = tax.field_is_tax_inclusive === '1' || tax.field_is_tax_inclusive === 'true' || tax.field_is_tax_inclusive === true;

          if (tax.field_calculation_method === 'PERCENTAGE') {
            const taxRate = parseFloat(tax.field_tax_value) / 100;

            if (isInclusive) {
              // This tax's share of the pre-tax base hiding inside gross.
              taxAmount = grossTuition * taxRate / (1 + inclusiveRateSum / 100);
            } else {
              // Add tax on top of gross tuition
              taxAmount = grossTuition * taxRate;
            }
          } else if (tax.field_calculation_method === 'FLAT_RATE') {
            taxAmount = parseFloat(tax.field_tax_value);
          }

          if (tax.field_rounding_rule) {
            taxAmount = this.applyRoundingRule(taxAmount, tax.field_rounding_rule);
          }

          // Persist the per-tax dollar amount onto the breakdown row so it's
          // stored alongside the rate in field_tax_breakdown.
          taxControl.patchValue({ field_tax_amount: Number(taxAmount.toFixed(2)) }, { emitEvent: false });

          totalTaxAmount += taxAmount;

          if (isInclusive) {
            inclusiveTaxAmount += taxAmount;
          } else {
            exclusiveTaxAmount += taxAmount;
            if (taxAmount > largestExclusiveAmount) {
              largestExclusiveAmount = taxAmount;
              largestExclusiveCtrl = taxControl;
            }
          }
        });

        // Sum the configured tax rates instead of reverse-calculating from amounts
        effectiveTaxRate = taxBreakdownArray.controls.reduce((sum, ctrl) => {
          return sum + (parseFloat(ctrl.value.field_tax_value) || 0);
        }, 0);
      }

      // Calculate total: gross + only the exclusive taxes (inclusive already in gross)
      let totalPaymentAmount = grossTuition + exclusiveTaxAmount;

      // Staff typed the tax-inclusive Total: gross was derived from it and
      // re-rounded, so the forward total can land a cent off the typed figure
      // ($150.00 → gross $136.39 → $149.99). Keep the figure they typed and
      // absorb the remainder into the largest tax's collected amount, the
      // standard tax-included split. A larger drift means the inputs changed
      // materially since the Total was typed, so the lock is released.
      if (this.lockedTotalFromEntry !== null) {
        const diff = Math.round((this.lockedTotalFromEntry - totalPaymentAmount) * 100) / 100;
        if (Math.abs(diff) > (this.grossSnappedToScheduledRow ? 0.10 : 0.02)) {
          this.lockedTotalFromEntry = null;
        } else {
          if (diff !== 0 && largestExclusiveCtrl) {
            const rowAmount = parseFloat(largestExclusiveCtrl.value?.field_tax_amount) || 0;
            largestExclusiveCtrl.patchValue({ field_tax_amount: Math.round((rowAmount + diff) * 100) / 100 }, { emitEvent: false });
            totalTaxAmount = Math.round((totalTaxAmount + diff) * 100) / 100;
            exclusiveTaxAmount += diff;
          }
          totalPaymentAmount = this.lockedTotalFromEntry;
        }
      }

      // Staff typed the pre-tax amount that pays the enrollment off: collect
      // the tax the enrollment still owes rather than gross x rate. The
      // estimate was rounded once on the full price ($8,662.50 x 13% =
      // $1,126.125 -> $1,126.13) while each payment rounds its own share
      // ($575.22 + $550.90 = $1,126.12), so the forward figure can clear the
      // pre-tax price yet leave a cent of tax that the enrollment list then
      // shows as $0.01 due and no payment can ever settle. Only a 1-2 cent
      // gap is that residue; anything larger means the amounts don't line up
      // and the forward split stands.
      if (this.lockedTotalFromEntry === null && largestExclusiveCtrl) {
        const closingTax = this.closingPaymentTax(grossTuition);
        if (closingTax !== null) {
          const diff = Math.round((closingTax - exclusiveTaxAmount) * 100) / 100;
          if (diff !== 0 && Math.abs(diff) <= 0.02) {
            const rowAmount = parseFloat(largestExclusiveCtrl.value?.field_tax_amount) || 0;
            largestExclusiveCtrl.patchValue({ field_tax_amount: Math.round((rowAmount + diff) * 100) / 100 }, { emitEvent: false });
            totalTaxAmount = Math.round((totalTaxAmount + diff) * 100) / 100;
            exclusiveTaxAmount = Math.round((exclusiveTaxAmount + diff) * 100) / 100;
            totalPaymentAmount = Math.round((grossTuition + exclusiveTaxAmount) * 100) / 100;
          }
        }
      }

      LoggerService.log('Tax calculation summary:', {
        grossTuition,
        inclusiveTaxAmount,
        exclusiveTaxAmount,
        totalTaxAmount,
        totalPaymentAmount
      });

      this.paymentForm.patchValue({
        field_total_payment_amount: totalPaymentAmount.toFixed(2),
        field_tax_rate_at_time_of_pay: parseFloat(effectiveTaxRate.toFixed(3)),
        field_tax_collected: totalTaxAmount.toFixed(2)
      }, { emitEvent: false });

    } finally {
      this.isUpdatingTaxes = false;
    }
  }

  private applyRoundingRule(amount: number, rule: string): number {
    // Snap in cent space before rounding: an exact half cent ($100 × 9.975% =
    // $9.975) must round by the rule, not by its binary representation
    // ($9.97499999…), and artifacts like 40.00000000000004 must not ceil up.
    const cents = Math.round(amount * 100 * 10000) / 10000;
    switch (rule) {
      case 'ROUND_UP':
        return Math.ceil(cents) / 100;
      case 'ROUND_DOWN':
        return Math.floor(cents) / 100;
      case 'ROUND_NEAREST':
      case 'NO_ROUNDING':
      default:
        return Math.round(cents) / 100;
    }
  }

  addTaxBreakdown() {
    const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;
    const newTaxGroup = this._formBuilder.group({
      field_tax_type: [null, Validators.required],
      field_tax_value: [null, Validators.required],
      field_calculation_method: [null, Validators.required],
      field_rounding_rule: [null, Validators.required],
      // Computed dollar amount for this tax, written by updateTaxesAndTotal().
      field_tax_amount: [null],
    });

    // Subscribe to changes in the new tax group
    newTaxGroup.valueChanges.subscribe(() => {
      this.updateTaxesAndTotal();
    });

    taxBreakdownArray.push(newTaxGroup);
  }

  removeTaxBreakdown(index: number) {
    const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;
    taxBreakdownArray.removeAt(index);

    // Trigger tax recalculation after removing a tax breakdown entry
    this.updateTaxesAndTotal();
  }

  getTaxBreakdownControls() {
    return (this.paymentForm.get('field_tax_breakdown') as FormArray).controls;
  }

  override ngAfterViewInit() {
    setTimeout(() => {
      this._loadFieldsData(this.injectedDialogData['data']['fieldsData'], this.paymentForm);

      if (this.initActionType == 'create' || this.initActionType == 'delete' || this.initActionType == 'refund') {
        this.getUniquePaymentID().subscribe(data => {
          this.paymentID = data;
          if (this.initActionType == 'refund') {
            this.paymentSaleDate = moment();
            this._loadFieldsData(this.injectedDialogData['data']['fieldsData'], this.paymentForm.value);

            if (this.paymentForm.value.field_payment_type != 1407 && this.paymentForm.value.field_status != 'Refund') {
              this.paymentForm.controls['field_date_and_time'].reset();
              this.paymentForm.controls['field_payment_type'].reset();
            }

            this.paymentForm.controls['reason_to_refund'].setValue('requested_by_customer');
          }
        });
      }
    }, 500);
  }

  checkEnrollmentPayments() {
    const enrollmentPayments = this.paymentForm.get('field_enrollment_name').value || [];
    let grossTuition = Number(this.paymentForm.get('field_gross_tuition').value) || 0;

    // Handle refund case by taking the absolute value of grossTuition
    if (this.initActionType === 'refund') {
      return;
    }

    let totalEnrollmentPayments = 0;

    if (Array.isArray(enrollmentPayments)) {
      totalEnrollmentPayments = enrollmentPayments.reduce((sum, enrollment) => sum + (Number(enrollment.payment_amount) || 0), 0);
    } else {
      totalEnrollmentPayments = Number(enrollmentPayments.payment_amount) || 0;
    }

    if (totalEnrollmentPayments !== grossTuition) {
      this.enrollmentPaymentError = "The total of all enrollment payments must match the gross tuition.";
    } else {
      this.enrollmentPaymentError = '';
    }
  }

  onSubmitPayment(f: NgForm, drop = false) {
    if (!this.paymentIdempotencyKey) {
      this.paymentIdempotencyKey = `payment_${new Date().getTime()}_${Math.random().toString(36).slice(2, 10)}`;
    }
    const idempotencyKey = this.paymentIdempotencyKey;
    this.isSubmitting = true;
    this.displayProgressSpinner(true);

    this.checkEnrollmentPayments();
    if (this.enrollmentPaymentError) {
      this.displayProgressSpinner(false);
      this.isSubmitting = false;
      return;
    }

    if (this.initActionType == "create" || this.initActionType == "refund") {
      this.errorMessage = "";
      let values = _.cloneDeep(f.value);
      this.__alterValues(values);

      if (this.initActionType == "create") {
        values.enrollment_ids = values.field_enrollment_name;
        values.enrollment_payments = values.field_enrollment_name.map(enrollment => {
          return {
            enrollment_id: enrollment.target_id,
            payment_amount: enrollment.payment_amount
          };
        });
      } else {
        values.enrollment_ids = [values.field_enrollment_name];
        values.field_gross_tuition = -(Math.abs(values.field_gross_tuition));
        values.field_stripe_charge_id = this.entityData?.field_stripe_charge_id;
        // The tax and customer-facing total reverse with the tuition. The
        // server prorates them from the original payment when they're absent.
        values.field_tax_collected = -Math.abs(parseFloat(values.field_tax_collected) || 0);
        values.field_total_payment_amount = -Math.abs(parseFloat(values.field_total_payment_amount) || 0);
        values.original_payment_id = this.entityId;
      }

      if (this.initActionType == "refund") {
        this.paymentForm.value.field_gross_tuition = -(Math.abs(this.paymentForm.value.field_gross_tuition));
        this.paymentForm.value.field_tax_collected = -Math.abs(parseFloat(this.paymentForm.value.field_tax_collected) || 0);
        this.paymentForm.value.field_total_payment_amount = -Math.abs(parseFloat(this.paymentForm.value.field_total_payment_amount) || 0);
      }

      let body = {
        ...values,
        idempotency_key: idempotencyKey
      };

      // A PayBright refund must reverse at the gateway (void/refund), not just
      // record a negative entity. Detect it from the ORIGINAL payment's stored
      // gateway reference rather than the form's payment-type value, because the
      // form resets field_payment_type to null when a refund dialog opens. So
      // a tid match would be skipped and the refund would silently fall through
      // to a record-only negative entity (money never returned). Falling back to
      // the resolved tid covers any case where the reference wasn't captured.
      const paymentTypeValue = this.paymentForm.get('field_payment_type').value;
      const isPaybrightOriginal =
        !!this.entityData?.field_paybright_reference_number
        || (this.paybrightPaymentTypeId != null && Number(paymentTypeValue) === this.paybrightPaymentTypeId);
      if (this.initActionType == 'refund' && isPaybrightOriginal) {
        this.processPaybrightRefund(body, drop);
        return;
      }

      // Handle different payment types
      switch (paymentTypeValue) {
        case '4': // 4 is the ID for Square payments
          // this.squareWebPayment.processPayment();
          this.processDefaultPayment(body, drop);
          break;
        case 1407: // Stripe. The select holds a string, so match both forms.
        case '1407':
          // Stripe is charged inside postPayment; there is no separate
          // createStripePayment endpoint. The numeric case never matched, so
          // Stripe always went through the default path anyway.
          this.processDefaultPayment(body, drop);
          break;
        default:
          this.processDefaultPayment(body, drop);
          break;
      }
    } else {
      this.onSubmit(this.paymentForm);
    }
  }

  private processDefaultPayment(body: any, drop: boolean) {
    this._drupalRESTService.httpPOST('/api_rest/v1/postPayment', body).subscribe(
      data => this.handlePaymentSuccess(data, drop),
      error => this.handlePaymentError(error)
    );
  }

  private handlePaymentError(error: any) {
    LoggerService.error("Payment error:", error);

    // The server answered, so that attempt is finished (and, for Stripe,
    // rolled back and refunded). The next submit must be a new charge. On a
    // network failure (status 0) the key is kept so the retry is idempotent.
    if (typeof error?.status === 'number' && error.status > 0) {
      this.paymentIdempotencyKey = null;
    }

    // Default values
    let errorMessage = "Unknown error occurred";
    let duration = 5000;

    // Handle enhanced error object with duration
    if (error && error.duration) {
      duration = error.duration;
    }

    // Check for validation errors from payment processing (nested structure)
    if (error && error.error && error.error.error && typeof error.error.error === 'string') {
      errorMessage = error.error.error;
    }
    // Check for direct error message (string in error.error)
    else if (error && error.error && typeof error.error === 'string') {
      errorMessage = error.error;
    }
    // Handle HttpErrorResponse
    else if (error && error.error && error.error.message) {
      // API returned error message
      errorMessage = error.error.message;
    } else if (error && error.message) {
      // Direct error message
      errorMessage = error.message;
    }

    // Special handling for specific error messages
    if (errorMessage === "Enrollment has already been paid for.") {
      errorMessage = "This enrollment has already been paid for. Please refresh the page to see the updated status.";
      duration = 8000;
    } else if (errorMessage === "Payment amount cannot be more than total scheduled payments for all posted enrollments.") {
      errorMessage = "The payment amount exceeds the total amount due. Please verify the payment amount matches the scheduled payments.";
      duration = 8000;
    } else if (/refund/i.test(errorMessage)) {
      // The server refunded (or failed to refund) a card charge; staff need
      // time to read which it was before deciding whether to resubmit.
      duration = 20000;
    } else if (error && error.status === 406 && errorMessage === "Unknown error occurred") {
      // Only fall back to generic wording when the server gave no reason;
      // a 406 normally carries the exact validation message.
      errorMessage = "The payment request was not accepted. Please verify all payment details are correct and try again.";
      duration = 8000;
    }

    LoggerService.log('errorMessage', errorMessage);
    this.errorMessage = errorMessage;
    this.isSubmitting = false;
    this.displayProgressSpinner(false);

    this._snackBar.open(`Payment failed: ${errorMessage}`, 'Close', { duration: duration });
  }

  private handlePaymentSuccess(data: any, drop: boolean) {
    this.isSubmitting = false;
    this.displayProgressSpinner(false);
    this.paymentIdempotencyKey = null;

    // Set payment success data for receipt tab
    this.paymentSuccess = true;

    // Create a properly formatted receiptData object that matches PaymentEntity structure
    this.receiptData = {
      field_payment_id: this.paymentForm.value.field_payment_id,
      field_date_and_time: this.paymentForm.value.field_date_and_time,
      field_gross_tuition: this.paymentForm.value.field_gross_tuition,
      field_tax_collected: this.paymentForm.value.field_tax_collected,
      field_total_payment_amount: this.paymentForm.value.field_total_payment_amount,
      field_reference_number: data.field_reference_number || '',
      field_status: 'Completed',
      field_customer_name: this.getCustomerNameFromForm(),

      // Handle entity references with proper structure
      field_student_name: {
        target_id: this.regexStudentName(this.paymentForm.value.field_student_name),
        name: this.paymentForm.value.field_student_name
      },

      field_payment_type: {
        target_id: this.paymentForm.value.field_payment_type,
        name: this.getPaymentTypeName(this.paymentForm.value.field_payment_type)
      }
    };

    // Handle enrollment references
    if (Array.isArray(this.paymentForm.value.field_enrollment_name) &&
        this.paymentForm.value.field_enrollment_name.length > 0) {
      // Use the first enrollment for the receipt
      const enrollment = this.paymentForm.value.field_enrollment_name[0];
      this.receiptData.field_enrollment_name = {
        target_id: enrollment.target_id,
        name: this.getEnrollmentName(enrollment.target_id)
      };
    } else if (this.paymentForm.value.field_enrollment_name?.target_id) {
      this.receiptData.field_enrollment_name = {
        target_id: this.paymentForm.value.field_enrollment_name.target_id,
        name: this.getEnrollmentName(this.paymentForm.value.field_enrollment_name.target_id)
      };
    }

    // Include server response data
    if (data && typeof data === 'object') {
      this.receiptData = {
        ...this.receiptData,
        ...data
      };
    }

    // The receipt's email button needs the payment ENTITY id; the receipt
    // number (field_payment_id) is not unique and cannot stand in for it.
    // postPayment returns the entity id as paymentId; the card gateways as id.
    this.receiptData.id = data?.id ?? data?.paymentId ?? null;

    LoggerService.log('Receipt data:', this.receiptData);

    // Show receipt tab
    this.selectedIndex = 3; // Set to Receipt tab index

    if (drop) {
      this.handleEnrollmentDrop();
    } else if (this.isNested) {
      this.paymentSubmitted.emit(data);
    }

    // Don't close the dialog - let the user view the receipt
  }

  // Helper method to get enrollment name
  private getEnrollmentName(enrollmentId: string | number): string {
    const enrollment = this.availableEnrollmentsForUser?.find(e => e.id[0]?.value == enrollmentId);
    return enrollment ? enrollment.title[0]?.value : 'N/A';
  }

  // Helper method to get payment type name
  private getPaymentTypeName(paymentTypeId: string | number): string {
    // payment_type_all: existing payments may use a method the studio has
    // since hidden from the picker.
    const paymentType = this._taxonomyService.payment_type_all?.find(p => p.id == paymentTypeId);
    return paymentType ? paymentType.name : 'N/A';
  }

  /**
   * The payment types offered in the radio list: the studio's visible methods,
   * plus the currently-selected one when it is hidden (editing or refunding an
   * older payment must still show, and gate on, its original method).
   */
  get visiblePaymentTypes(): any[] {
    // PayBright ACH is restricted to super-admins while it is being proven
    // out; everyone else never sees the card (an existing ACH payment being
    // edited or refunded still shows via the hidden-selected path below).
    const visible = (this._taxonomyService.payment_type || []).filter(
      term => this.canUsePaybrightAch || this.normalizeTermName(term.name) !== 'paybright ach'
    );
    const selected = this.paymentForm?.get('field_payment_type')?.value;
    if (selected == null || visible.some(term => term.id == selected)) {
      return visible;
    }
    const hiddenSelected = this._taxonomyService.payment_type_all.find(term => term.id == selected);
    return hiddenSelected ? [...visible, hiddenSelected] : visible;
  }

  /** Whether the current user may take PayBright ACH payments (super-admins only). */
  get canUsePaybrightAch(): boolean {
    return this._permissionsService.isSuperAdmin();
  }

  // Helper method to extract customer name from form
  private getCustomerNameFromForm(): string {
    const studentName = this.paymentForm.value.field_student_name || '';
    // Extract name part without the ID
    const match = studentName.match(/(.*?)\s*\([^)]*\)$/);
    return match ? match[1].trim() : studentName;
  }

  private handleEnrollmentDrop() {
    let params = [
      { parameter: 'enrollment_id', value: this.paymentForm.value.field_enrollment_name.target_id },
      { parameter: 'drop_date', value: moment(this.paymentForm.value.field_date_and_time).format("YYYY/MM/DD") },
    ];
    this._drupalRESTService.httpGET('/api_rest/v1/dropEnrollment', params).subscribe(
      data => {
        this.displayProgressSpinner(false);
        if (data['success']) {
          this.closeDialog();
        }
      },
      error => {
        this.isSubmitting = false;
        this.errorMessage = "There was a problem dropping this enrollment.";
        this.handleError(error);
        this.displayProgressSpinner(false);
      }
    );
  }

  /**
   * Reverses a PayBright payment at the gateway (void/refund) and records the
   * negative refund entity server-side. The backend reads the original payment
   * (by entity id) for the gateway reference, so we only send the id, the
   * refund amount, and the reason.
   */
  private processPaybrightRefund(body: any, drop: boolean) {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.handlePaymentError({ message: 'No studio selected for the refund.' });
      return;
    }

    const refundBody = {
      paymentId: this.entityId,
      amount: Math.abs(this.paymentForm.value.field_gross_tuition),
      reason: this.paymentForm.value.reason_to_refund || 'requested_by_customer',
    };

    this._drupalRESTService.httpPOST(`/api/paybright/refund/${studioId}`, refundBody).subscribe(
      data => this.handlePaymentSuccess(data, drop),
      error => this.handlePaymentError(error)
    );
  }

  private processStripePayment(body: any, drop: boolean) {
    // Implement Stripe-specific payment logic here
    // This should be similar to the existing logic in your current onSubmitPayment method
    this.createStripePayment(body, drop);
  }

  private createStripePayment(body: any, drop: boolean) {
    // Implement Stripe payment creation logic
    // This should be similar to your existing Stripe payment creation process
    this._drupalRESTService.httpPOST('/api_rest/v1/createStripePayment', body).subscribe(
      data => this.handlePaymentSuccess(data, drop),
      error => this.handlePaymentError(error)
    );
  }

  override onDeleteSubmit(form: NgForm) {
    this.displayProgressSpinner(true);
    this._entityRESTService.getEntity(this.eckType, this.bundle, this.entityId).subscribe(data => {
      let body = data;
      body = {
        field_status: 'Deleted',
        field_reason_to_delete: this.paymentDeleteForm.value.field_reason_to_delete,
        field_enrollment_name: { target_id: data?.['field_enrollment_name']?.['id'] },
        field_student_name: data?.['field_student_name']?.['id'],
      };

      this._entityRESTService.patchEntity(this.eckType, this.bundle, this.entityId, body).subscribe(data => {
        this._entityRESTService.deleteEntity(this.eckType, this.bundle, this.entityId).subscribe(data => {
          this.displayProgressSpinner(false);
          this.closeDialog();
        }, error => this.handleError(error));

      }, error => this.handleError(error));
    });
  }

  splitAmountValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const grossTuition = this.paymentForm?.get('field_gross_tuition')?.value;
    if (control.value > grossTuition) {
      return { 'exceedsGrossTuition': true };
    }
    return null;
  }

  /**
   * Check if the selected payment type is "Check" or "Cheque"
   */
  isCheckPaymentType(): boolean {
    const name = this.selectedPaymentTypeName();
    return name === 'check' || name === 'cheque';
  }

  /** "Cheque Number" for the Cheque payment type, "Check Number" otherwise. */
  checkNumberLabel(): string {
    return this.selectedPaymentTypeName() === 'cheque' ? 'Cheque Number' : 'Check Number';
  }

  private selectedPaymentTypeName(): string | undefined {
    const paymentType = this._taxonomyService.payment_type_all?.find(
      pt => pt.id == this.paymentForm?.get('field_payment_type')?.value
    );
    return paymentType?.name?.toLowerCase();
  }

  getIconName(paymentType: string): string {
    switch (paymentType) {
      case 'Cash Payment':
        return 'attach_money';
      case 'Check':
      case 'Cheque':
        return 'check';
      case 'Bancomat':
      case 'Credit':
      case 'Credit Card':
      case 'Debit':
        return 'credit_card';
      case 'ACH':
      case 'PayBright ACH':
      case 'Bank Transfer':
        return 'account_balance';
      case 'Exempt Adjustment':
        return 'money_off';
      case 'Gift Certificate':
        return 'redeem';
      case 'Other':
        return 'more_horiz';
      case 'PayBright Terminal':
      case 'Square Terminal':
        return 'point_of_sale';
      case 'Square':
        return 'crop_square';
      case 'Venmo':
        return 'payments';
      case 'E-Transfer':
      case 'Zelle':
        return 'account_balance';
      default:
        return 'payment';
    }
  }

  /**
   * True when this method is the currently-selected one AND it has extra
   * options to show (check number, saved cards / terminal choice, refund
   * reason): drives the expand-open panel rendered after its card.
   */
  paymentTypeHasSubOptions(element: any): boolean {
    const selected = this.paymentForm?.get('field_payment_type')?.value;
    if (selected == null || element.id != selected) { return false; }
    if (this.isCheckPaymentType()) { return true; }
    const isRefund = this.initActionType == 'refund';
    if (String(selected) === '1407') {
      // Stripe: payment options on create/edit, refund reason on refund.
      return !isRefund || this.entityData?.field_status != 'Refund';
    }
    if (String(selected) === '1422') { return !isRefund; }
    if (this.isGiftCertificateSelected) { return !isRefund; }
    if (this.isPaybrightAchSelected) { return this.canUsePaybrightAch && !isRefund; }
    return this.isPaybrightTerminalSelected && !isRefund;
  }

  /** Open the payment method settings in a dialog over the payment form.
   *  The picker updates live after saving via TaxonomyOverridesService. */
  openPaymentMethodSettings() {
    this._dialogService.openDialog(PaymentMethodsDialogComponent, 'default');
  }

  /** Font Awesome brand icon for a payment type, or null when a Material
   *  icon from getIconName() is used instead. Same fa-brands set as the
   *  saved-card lists below the picker. */
  getBrandIcon(paymentType: string): string | null {
    switch (paymentType) {
      case 'AMEX':
        return 'fa-cc-amex';
      case 'Discover':
        return 'fa-cc-discover';
      case 'MasterCard':
        return 'fa-cc-mastercard';
      case 'PayPal':
        return 'fa-paypal';
      case 'Stripe':
        return 'fa-stripe-s';
      case 'Visa':
        return 'fa-cc-visa';
      default:
        return null;
    }
  }

  loadTaxRatesFromConfig() {
    if (this.initActionType === 'edit') {
      LoggerService.log('Edit mode: Preserving existing tax values');
      return;
    }

    this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').subscribe(
      (response: any) => {
        LoggerService.log('Tax configuration response:', response);

        if (!response || (Array.isArray(response) && response.length === 0)) {
          LoggerService.log('No tax configuration found');
          this.handleNoTaxConfig(false);
          return;
        }

        const taxConfig = Array.isArray(response) ? response : [response];
        this.taxConfigRows = taxConfig;
        this.setTaxBreakdown(taxConfig, false);
      },
      error => {
        LoggerService.error('Error loading tax rates:', error);
        this.handleNoTaxConfig(false);
      }
    );
  }

  loadTaxRatesFromEntity() {
    if (!this.entityData || this.initActionType !== 'edit') {
      LoggerService.log('Not in edit mode or no entity data available');
      return;
    }

    LoggerService.log('Loading tax rates from entity:', this.entityData);

    if (this.entityData.field_tax_breakdown) {
      try {
        let taxBreakdown = JSON.parse(this.entityData.field_tax_breakdown);
        this.setTaxBreakdown(taxBreakdown, true); // true indicates it's edit mode
      } catch (e) {
        LoggerService.error('Error parsing tax breakdown from entity:', e);
        this.handleNoTaxConfig(true); // true indicates it's edit mode
      }
    } else {
      LoggerService.warn('No tax breakdown found in entity data');
      this.handleNoTaxConfig(true); // true indicates it's edit mode
    }

    // Set other tax-related fields
    this.paymentForm.patchValue({
      field_tax_rate_at_time_of_pay: this.displayRate(this.entityData.field_tax_rate_at_time_of_pay),
      field_tax_collected: this.entityData.field_tax_collected,
      field_total_payment_amount: this.entityData.field_total_payment_amount
    });
  }

  updateTotalPaymentAmount() {
    const grossTuition = Number(this.paymentForm.get('field_gross_tuition').value) || 0;
    const taxBreakdownArray = this.paymentForm.get('field_tax_breakdown') as FormArray;

    let inclusiveTaxAmount = 0;
    let exclusiveTaxAmount = 0;

    // Process tax breakdown to separate inclusive vs exclusive
    if (taxBreakdownArray && taxBreakdownArray.length > 0) {
      taxBreakdownArray.controls.forEach((taxControl: AbstractControl) => {
        const tax = taxControl.value;
        if (!tax.field_tax_value || !tax.field_calculation_method) {
          return;
        }

        const isInclusive = tax.field_is_tax_inclusive === '1' || tax.field_is_tax_inclusive === 'true' || tax.field_is_tax_inclusive === true;
        let taxAmount = 0;

        if (tax.field_calculation_method === 'PERCENTAGE') {
          const taxRate = parseFloat(tax.field_tax_value) / 100;

          if (isInclusive) {
            taxAmount = grossTuition - (grossTuition / (1 + taxRate));
          } else {
            taxAmount = grossTuition * taxRate;
          }
        } else if (tax.field_calculation_method === 'FLAT_RATE') {
          taxAmount = parseFloat(tax.field_tax_value);
        }

        if (tax.field_rounding_rule) {
          taxAmount = this.applyRoundingRule(taxAmount, tax.field_rounding_rule);
        }

        if (isInclusive) {
          inclusiveTaxAmount += taxAmount;
        } else {
          exclusiveTaxAmount += taxAmount;
        }
      });
    }

    const totalTaxAmount = inclusiveTaxAmount + exclusiveTaxAmount;
    const totalPaymentAmount = grossTuition + exclusiveTaxAmount;

    this.paymentForm.patchValue({
      field_total_payment_amount: totalPaymentAmount.toFixed(2),
      field_tax_collected: totalTaxAmount.toFixed(2)
    });

    LoggerService.log('Gross Tuition:', grossTuition);
    LoggerService.log('Inclusive Tax:', inclusiveTaxAmount);
    LoggerService.log('Exclusive Tax:', exclusiveTaxAmount);
    LoggerService.log('Total Tax Amount:', totalTaxAmount);
    LoggerService.log('Total Payment Amount:', totalPaymentAmount);
  }

  createSquarePayment(token: string) {
    const paymentData = {
      sourceId: token,
      amount: this.paymentForm.get('field_total_payment_amount').value,
      // Add other necessary payment details
    };

    this._drupalRESTService.httpPOST('/api_rest/v1/createSquarePayment', paymentData).subscribe(
      (response) => {
        LoggerService.log('Square payment created:', response);
        this.closeDialog();
      },
      (error) => {
        LoggerService.error('Error creating Square payment:', error);
        this.handleError(error);
      }
    );
  }

  onSquarePaymentComplete(token: string) {
    const paymentData = {
      sourceId: token,
      amount: this.paymentForm.get('field_total_payment_amount').value,
      idempotency_key: `payment_${new Date().getTime()}`,
      // Add other necessary payment details from this.paymentForm
    };

    this._drupalRESTService.httpPOST('/api_rest/v1/createSquarePayment', paymentData).subscribe(
      data => this.handlePaymentSuccess(data, false),
      error => this.handlePaymentError(error)
    );
  }

  onSquarePaymentError(error: any) {
    LoggerService.error('Square Terminal payment error:', error);

    if (!error) {
      this.handlePaymentError({ message: "An unknown error occurred" });
    } else if (error.error?.error === 'No device ID configured for this studio.') {
      // Special handling for no device ID error
      this._snackBar.open(
        'Square Terminal payment failed: No device configured for this studio. Please contact your administrator.',
        'Close',
        { duration: 8000 }
      );
      this.handlePaymentError({
        message: "No Square Terminal device is configured for this studio. Please contact your administrator."
      });
    } else {
      // Parse Square-specific payment errors
      const squareError = this.parseSquarePaymentError(error);
      this.handlePaymentError(squareError);
    }
  }

  /**
   * Parse Square payment errors and return user-friendly error messages
   */
  private parseSquarePaymentError(error: any): any {
    // Default error message
    let errorMessage = "Payment failed. Please try again or contact support.";
    let duration = 8000;

    // Check for Square payment processing errors
    if (error.payment && error.payment.card_details && error.payment.card_details.errors) {
      const cardErrors = error.payment.card_details.errors;
      const errorCodes = cardErrors.map(err => err.code);
      const errorDetails = cardErrors.map(err => err.detail);

      if (errorCodes.includes('TRANSACTION_LIMIT')) {
        errorMessage = "This transaction exceeds your card's transaction limit. Please contact your bank or try a different payment method.";
        duration = 10000;
      } else if (errorCodes.includes('INSUFFICIENT_FUNDS')) {
        errorMessage = "Insufficient funds available. Please check your account balance or try a different payment method.";
        duration = 10000;
      } else if (errorCodes.includes('CARD_DECLINED')) {
        errorMessage = "Your card was declined. Please contact your bank or try a different payment method.";
        duration = 10000;
      } else if (errorCodes.includes('EXPIRED_CARD')) {
        errorMessage = "Your card has expired. Please use a current card or update your payment information.";
        duration = 10000;
      } else if (errorCodes.includes('INVALID_CARD')) {
        errorMessage = "Invalid card information. Please check your card details and try again.";
        duration = 10000;
      } else if (errorCodes.includes('CVV_FAILURE')) {
        errorMessage = "Card security code (CVV) verification failed. Please check your CVV and try again.";
        duration = 10000;
      } else if (errorCodes.includes('ADDRESS_VERIFICATION_FAILURE')) {
        errorMessage = "Address verification failed. Please verify your billing address and try again.";
        duration = 10000;
      } else {
        // Generic card error with details
        errorMessage = `Payment failed: ${errorDetails.join(', ')}. Please try a different payment method or contact support.`;
        duration = 10000;
      }
    } else if (error.error && typeof error.error === 'string') {
      // Handle other Square API errors
      errorMessage = `Payment failed: ${error.error}`;
    } else if (error.message) {
      // Handle generic error messages
      errorMessage = error.message;
    }

    return {
      message: errorMessage,
      duration: duration,
      error: error
    };
  }

  getCustomerInfo(): any {
    return {
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "5551234567",
      address1: "123 Maple Street",
      address2: "Apt 4B",
      city: "Springfield",
      state: "IL",
      country: "US",
    };
    // return {
    //   firstName: this.paymentForm.get('field_first_name')?.value,
    //   lastName: this.paymentForm.get('field_last_name')?.value,
    //   email: this.paymentForm.get('field_email')?.value,
    //   phone: this.paymentForm.get('field_phone')?.value,
    //   address1: this.paymentForm.get('field_address_1')?.value,
    //   address2: this.paymentForm.get('field_address_2')?.value,
    //   city: this.paymentForm.get('field_city')?.value,
    //   state: this.paymentForm.get('field_state')?.value,
    //   country: this.paymentForm.get('field_country')?.value,
    // };
  }

  onSquareTerminalPaymentComplete(event: any) {
    LoggerService.log('Square Terminal payment completed:', event);

    if (event?.error) {
      LoggerService.error("Square Terminal error:", event.error);
      this.handlePaymentError(event.error);
    } else {
      this.handlePaymentSuccess(event, false);
    }
  }

  onSquareTerminalPaymentError(error: any) {
    LoggerService.error('Square Terminal payment error:', error);

    if (!error) {
      this.handlePaymentError({ message: "An unknown error occurred" });
    } else if (error.error?.error === 'No device ID configured for this studio.') {
      // Special handling for no device ID error
      this._snackBar.open(
        'Square Terminal payment failed: No device configured for this studio. Please contact your administrator.',
        'Close',
        { duration: 8000 }
      );
      this.handlePaymentError({
        message: "No Square Terminal device is configured for this studio. Please contact your administrator."
      });
    } else {
      // Parse Square-specific payment errors
      const squareError = this.parseSquarePaymentError(error);
      this.handlePaymentError(squareError);
    }
  }

  openSquareTerminalPayment() {
    const amount = this.paymentForm.get('field_total_payment_amount').value;
    const selectedCard = this.paymentForm.get('field_payments_use_card').value;
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);

    // Basic validation
    if (!amount || amount <= 0) {
      this._snackBar.open('Invalid payment amount. Please enter a valid amount.', 'Close', { duration: 5000 });
      return;
    }

    const dialogData = {
      amount: amount,
      currency: 'USD',
      enrollmentIds: this.paymentForm.get('field_enrollment_name')?.value,
      studentAccountId: studentId,
      studioId: this._authService.selectedStudio?.id,
      paymentForm: this.paymentForm,
      savedCardId: selectedCard === 'terminal_payment' ? null : selectedCard,
      idempotencyKey: uuidv4(),
    };

    LoggerService.log('Opening Square Terminal Payment dialog with data:', dialogData);

    const dialogRef = this._dialogService.openDialog(
      SquareTerminalPaymentComponent,
      'fullScreenCreditCardInput',
      dialogData
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.status === 'SUCCESS') {
          this.onSquareTerminalPaymentComplete(result);
        } else if (result.status === 'FAILURE') {
          this.onSquareTerminalPaymentError(result.error);
        } else if (result.status === 'CANCELED') {
          this._snackBar.open('Payment was canceled', 'Close', { duration: 5000 });
        }
      }
    });
  }

  /** Normalizes a taxonomy name for matching: lowercased, trimmed, internal
   *  whitespace collapsed. */
  private normalizeTermName(name: any): string {
    return (name || '').toString().toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /**
   * The PayBright Terminal payment_type term id, resolved from the loaded
   * taxonomy by name so it is correct regardless of the per-database term id.
   * Returns null until the taxonomy has loaded.
   */
  get paybrightPaymentTypeId(): number | null {
    // Resolved against the unfiltered list: a studio hiding the method from
    // the picker must not break gating on an existing PayBright payment.
    const term = this._taxonomyService.payment_type_all?.find(
      p => this.normalizeTermName(p.name) === 'paybright terminal'
    );
    return term ? Number(term.id) : null;
  }

  /**
   * True when the currently-selected payment type is PayBright Terminal.
   *
   * Resolves by matching the SELECTED term's own name (not just the
   * separately-resolved id) so a taxonomy rename can never desync the gating in
   * a way that silently falls back to the normal "Save Payment" button (which
   * would record a payment without charging). Either signal (id match or the
   * selected option's name being "PayBright Terminal") counts as selected.
   */
  get isPaybrightTerminalSelected(): boolean {
    const selected = this.paymentForm?.get('field_payment_type')?.value;
    if (selected == null) {
      return false;
    }
    if (this.paybrightPaymentTypeId != null && Number(selected) === this.paybrightPaymentTypeId) {
      return true;
    }
    const selectedTerm = this._taxonomyService.payment_type_all?.find(p => Number(p.id) === Number(selected));
    return !!selectedTerm && this.normalizeTermName(selectedTerm.name) === 'paybright terminal';
  }

  openPaybrightTerminalPayment() {
    const amount = this.paymentForm.get('field_total_payment_amount').value;
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);

    if (!amount || amount <= 0) {
      this._snackBar.open('Invalid payment amount. Please enter a valid amount.', 'Close', { duration: 5000 });
      return;
    }

    // Reuse field_payments_use_card for the saved-card vs terminal selection
    // (same control Square uses). 'terminal_payment' (or empty) => take the card
    // on the terminal; any other value is a saved payment method id.
    const selectedCard = this.paymentForm.get('field_payments_use_card')?.value;
    const savedCardId = (selectedCard && selectedCard !== 'terminal_payment') ? selectedCard : null;

    const dialogData = {
      amount: amount,
      currency: 'USD',
      enrollmentIds: this.paymentForm.get('field_enrollment_name')?.value,
      studentAccountId: studentId,
      studioId: this._authService.selectedStudio?.id,
      paymentForm: this.paymentForm,
      idempotencyKey: uuidv4(),
      savedCardId: savedCardId,
      // Only meaningful on a terminal charge; ignored when paying a saved card.
      saveCard: !savedCardId && !!this.paybrightSaveCard,
    };

    const dialogRef = this._dialogService.openDialog(
      PaybrightTerminalPaymentComponent,
      'fullScreenCreditCardInput',
      dialogData
    );

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      if (result.status === 'APPROVED' || result.status === 'PARTIALLY_APPROVED') {
        // Build the receipt data. Use the payment id the backend stored (the
        // form's pre-fetched id is NOT what the terminal controller saves), the
        // real card last-4 (not the gateway reference number, which is a
        // transaction id), and the gateway card surcharge so the receipt can
        // itemise it and show what was actually charged to the card.
        const successData: any = {
          ...result,
          field_payment_id: result.payment_id || this.paymentForm.value.field_payment_id,
          field_reference_number: '',
          field_card_last4: result.last_4 ? String(result.last_4) : '',
          field_card_type: result.card_type || '',
          field_surcharge: Number(result.surcharge) > 0 ? Number(result.surcharge).toFixed(2) : '',
          field_paybright_reference_number: result.referenceNumber || '',
        };
        // Warn on a partial approval so staff know to collect the remainder.
        if (result.status === 'PARTIALLY_APPROVED') {
          this._snackBar.open(
            `Card partially approved: ${result.captured_amount} of ${result.requested_amount} captured. Collect the remainder.`,
            'Close', { duration: 8000 }
          );
        }
        // If a card was just saved, refresh the saved-card list so it appears.
        if (result.saved_card) {
          this.loadPaybrightSavedCards();
        }
        // Clear the save-card intent so a subsequent charge doesn't save again.
        this.paybrightSaveCard = false;
        this.handlePaymentSuccess(successData, false);
      } else if (result.status === 'APPROVED_UNRECORDED') {
        // The card was charged but the local record failed to save. Do not show
        // a success receipt; tell staff to reconcile manually.
        this.handlePaymentError({
          message: result.error || 'The card was charged but the payment record could not be saved. Please reconcile manually using the reference number.'
        });
      } else if (result.status === 'DECLINED' || result.status === 'ERROR') {
        this.handlePaymentError(result.error ? { message: result.error } : { message: 'PayBright payment failed' });
      } else if (result.status === 'CANCELED') {
        this._snackBar.open('Payment was canceled', 'Close', { duration: 5000 });
      }
    });
  }

  /** Whether to tokenize and save the card taken on the terminal. */
  paybrightSaveCard: boolean = false;

  /**
   * Whether a PayBright payment method is selected (a saved card or "Process
   * with Terminal"). Gates the charge button so staff must choose first.
   */
  isPaybrightPaymentMethodSelected(): boolean {
    const selected = this.paymentForm.get('field_payments_use_card')?.value;
    return selected === 'terminal_payment'
      || (!!selected && this.paybright_payment_methods?.some(card => card.id == selected));
  }

  /** Reloads the PayBright saved cards for the currently-selected student. */
  loadPaybrightSavedCards() {
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);
    const studioId = this._authService.selectedStudio?.id;
    if (!studentId || !studioId) {
      return;
    }

    const endpoint = `/api/paybright/saved-cards/${studioId}`;
    const params = [{ parameter: 'field_student_name', value: studentId }];

    this._drupalRESTService.httpGET(endpoint, params).subscribe({
      next: (response: any) => {
        this.paybright_payment_methods = response.cards || [];
      },
      error: () => {
        this._snackBar.open('Failed to load saved cards', 'Close', { duration: 5000 });
      }
    });
  }

  /** Deletes a PayBright saved card (payment method). */
  delete_paybright_payment_method(cardId: string) {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this._snackBar.open('Missing studio to delete card', 'Close', { duration: 5000 });
      return;
    }
    if (!confirm('Are you sure you want to delete this saved payment method?')) {
      return;
    }

    const endpoint = `/api/paybright/delete-card/${studioId}/${cardId}`;
    this._drupalRESTService.httpDELETE(endpoint).subscribe({
      next: (response: any) => {
        if (response && response.success) {
          this._snackBar.open('Payment method deleted', 'Close', { duration: 5000 });
          if (this.paybright_payment_methods) {
            this.paybright_payment_methods = this.paybright_payment_methods.filter(card => card.id != cardId);
          }
          if (this.paymentForm.get('field_payments_use_card')?.value == cardId) {
            this.paymentForm.get('field_payments_use_card')?.setValue('terminal_payment');
          }
        } else {
          this._snackBar.open(response?.error || 'Failed to delete card', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this._snackBar.open(error?.error?.error || 'Failed to delete card', 'Close', { duration: 5000 });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // PayBright ACH (bank account debit)
  // ---------------------------------------------------------------------------

  /** Radio value in field_payments_use_card for "enter a new bank account". */
  readonly ACH_NEW_ACCOUNT = 'new_bank_account';

  /** The student's saved bank accounts at the gateway (check payment methods). */
  override paybright_bank_accounts: any[] = [];

  /** New bank account entry. Never persisted; sent once to the gateway. */
  achNewAccount = this.blankAchEntry();

  /** NACHA: the account holder must authorize each debit. */
  achAuthorizationAccepted = false;

  /** True while the ACH charge request is in flight. */
  achProcessing = false;

  private blankAchEntry() {
    return {
      routing_number: '',
      account_number: '',
      confirm_account_number: '',
      account_type: 'checking',
      name: '',
      save: true,
    };
  }

  resetAchEntry() {
    this.achNewAccount = this.blankAchEntry();
    this.achAuthorizationAccepted = false;
  }

  /**
   * The PayBright ACH payment_type term id, resolved by name from the
   * unfiltered taxonomy (same pattern as the terminal and gift certificate
   * types: a per-database id must never be hardcoded).
   */
  get paybrightAchPaymentTypeId(): number | null {
    const term = this._taxonomyService.payment_type_all?.find(
      p => this.normalizeTermName(p.name) === 'paybright ach'
    );
    return term ? Number(term.id) : null;
  }

  /**
   * True when the selected payment type is PayBright ACH. Matches by id or by
   * the selected term's own name so a rename cannot silently fall back to the
   * plain Save Payment button (which would record a payment without debiting).
   */
  get isPaybrightAchSelected(): boolean {
    const selected = this.paymentForm?.get('field_payment_type')?.value;
    if (selected == null) {
      return false;
    }
    if (this.paybrightAchPaymentTypeId != null && Number(selected) === this.paybrightAchPaymentTypeId) {
      return true;
    }
    const selectedTerm = this._taxonomyService.payment_type_all?.find(p => Number(p.id) === Number(selected));
    return !!selectedTerm && this.normalizeTermName(selectedTerm.name) === 'paybright ach';
  }

  /** Whether "enter a new bank account" is the chosen ACH option. */
  isNewBankAccountSelected(): boolean {
    return this.paymentForm.get('field_payments_use_card')?.value === this.ACH_NEW_ACCOUNT;
  }

  /** The chosen saved bank account id, or null when entering a new one. */
  private selectedSavedBankAccountId(): string | null {
    const selected = this.paymentForm.get('field_payments_use_card')?.value;
    if (!selected || selected === this.ACH_NEW_ACCOUNT) {
      return null;
    }
    return this.paybright_bank_accounts?.some(acct => acct.id == selected) ? String(selected) : null;
  }

  /** Nine digits with a valid ABA checksum (3-7-1 weights, mod 10). */
  isValidRoutingNumber(value: string): boolean {
    const digits = (value || '').replace(/\D/g, '');
    if (!/^\d{9}$/.test(digits)) {
      return false;
    }
    const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    const sum = digits.split('').reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    return sum % 10 === 0;
  }

  /** Shown under the routing field once the user has typed 9 digits. */
  achRoutingError(): string {
    const digits = (this.achNewAccount.routing_number || '').replace(/\D/g, '');
    if (digits.length === 9 && !this.isValidRoutingNumber(digits)) {
      return 'This routing number is not valid.';
    }
    return '';
  }

  achAccountMismatch(): boolean {
    const a = (this.achNewAccount.account_number || '').replace(/\D/g, '');
    const b = (this.achNewAccount.confirm_account_number || '').replace(/\D/g, '');
    return !!a && !!b && a !== b;
  }

  /** The new bank account entry is complete and self-consistent. */
  isAchNewAccountValid(): boolean {
    const acct = this.achNewAccount;
    const account = (acct.account_number || '').replace(/\D/g, '');
    return this.isValidRoutingNumber(acct.routing_number)
      && /^\d{4,17}$/.test(account)
      && !this.achAccountMismatch()
      && (acct.confirm_account_number || '').replace(/\D/g, '') === account
      && ['checking', 'savings'].includes(acct.account_type)
      && (acct.name || '').trim().length > 0;
  }

  /**
   * Gates the ACH charge button: a saved bank account is chosen, or a valid
   * new one is entered, and the account holder's authorization is ticked.
   */
  isPaybrightAchMethodSelected(): boolean {
    if (!this.achAuthorizationAccepted) {
      return false;
    }
    if (this.isNewBankAccountSelected()) {
      return this.isAchNewAccountValid();
    }
    return this.selectedSavedBankAccountId() !== null;
  }

  /** Reloads the PayBright saved bank accounts for the selected student. */
  loadPaybrightBankAccounts() {
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);
    const studioId = this._authService.selectedStudio?.id;
    if (!studentId || !studioId) {
      return;
    }
    const endpoint = `/api/paybright/bank-accounts/${studioId}`;
    const params = [{ parameter: 'field_student_name', value: studentId }];
    this._drupalRESTService.httpGET(endpoint, params).subscribe({
      next: (response: any) => {
        this.paybright_bank_accounts = response.bank_accounts || [];
        // Default to entering a new account when the student has none saved.
        if (!this.paybright_bank_accounts.length && !this.paymentForm.get('field_payments_use_card')?.value) {
          this.paymentForm.get('field_payments_use_card')?.setValue(this.ACH_NEW_ACCOUNT);
        }
      },
      error: () => {
        this._snackBar.open('Failed to load saved bank accounts', 'Close', { duration: 5000 });
      }
    });
  }

  /** Deletes a saved bank account (gateway payment method). */
  delete_paybright_bank_account(accountId: string) {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this._snackBar.open('Missing studio to delete bank account', 'Close', { duration: 5000 });
      return;
    }
    if (!confirm('Are you sure you want to delete this saved bank account?')) {
      return;
    }
    const endpoint = `/api/paybright/delete-card/${studioId}/${accountId}`;
    this._drupalRESTService.httpDELETE(endpoint).subscribe({
      next: (response: any) => {
        if (response && response.success) {
          this._snackBar.open('Bank account deleted', 'Close', { duration: 5000 });
          this.paybright_bank_accounts = (this.paybright_bank_accounts || []).filter(acct => acct.id != accountId);
          if (this.paymentForm.get('field_payments_use_card')?.value == accountId) {
            this.paymentForm.get('field_payments_use_card')?.setValue(this.ACH_NEW_ACCOUNT);
          }
        } else {
          this._snackBar.open(response?.error || 'Failed to delete bank account', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this._snackBar.open(error?.error?.error || 'Failed to delete bank account', 'Close', { duration: 5000 });
      }
    });
  }

  /**
   * Debits the chosen bank account through PayBright.
   *
   * No terminal and no dialog: the request returns the gateway's answer
   * directly. "Submitted" counts as approved (the debit is originated and the
   * payment is recorded Paid, so lessons unlock now); if the bank later
   * returns it, the webhook fails the payment and the studio is emailed.
   */
  processPaybrightAchPayment() {
    if (this.achProcessing) {
      return;
    }
    if (!this.canUsePaybrightAch) {
      this._snackBar.open('PayBright ACH payments are limited to super-admins.', 'Close', { duration: 5000 });
      return;
    }
    const amount = this.paymentForm.get('field_total_payment_amount').value;
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);
    const studioId = this._authService.selectedStudio?.id;
    if (!amount || amount <= 0) {
      this._snackBar.open('Invalid payment amount. Please enter a valid amount.', 'Close', { duration: 5000 });
      return;
    }
    if (!studentId || !studioId) {
      this._snackBar.open('Select a student before processing an ACH payment.', 'Close', { duration: 5000 });
      return;
    }
    if (!this.isPaybrightAchMethodSelected()) {
      this._snackBar.open('Choose a bank account and confirm the account holder authorized the debit.', 'Close', { duration: 5000 });
      return;
    }

    const amountInCents = Math.round(amount * 100);
    const paymentFormData = { ...this.paymentForm.value };
    if (paymentFormData.field_date_and_time) {
      paymentFormData.field_date_and_time = moment(paymentFormData.field_date_and_time.toString()).format('YYYY-MM-DD[T]HH:mm:ss');
    }

    const savedBankAccountId = this.selectedSavedBankAccountId();
    const payload: any = {
      enrollmentIds: this.paymentForm.get('field_enrollment_name')?.value,
      studentAccountId: studentId,
      currency: 'USD',
      amount: amountInCents,
      paymentForm: paymentFormData,
      idempotencyKey: uuidv4(),
      authorizationAccepted: true,
    };
    if (savedBankAccountId) {
      payload.savedBankAccountId = savedBankAccountId;
    } else {
      payload.bankAccount = {
        routing_number: this.achNewAccount.routing_number.replace(/\D/g, ''),
        account_number: this.achNewAccount.account_number.replace(/\D/g, ''),
        account_type: this.achNewAccount.account_type,
        name: this.achNewAccount.name.trim(),
      };
      payload.saveBankAccount = !!this.achNewAccount.save;
    }

    this.achProcessing = true;
    this.isSubmitting = true;
    const endpoint = `/api/paybright/ach/charge/${studioId}/${amountInCents}/USD`;
    this._drupalRESTService.httpPOST(endpoint, payload).subscribe({
      next: (response: any) => {
        this.achProcessing = false;
        this.isSubmitting = false;
        if (response?.status !== 'APPROVED') {
          this.handlePaymentError({ message: response?.error || 'PayBright did not accept the bank debit.' });
          return;
        }
        const last4 = response.last_4 ? String(response.last_4) : (this.achNewAccount.account_number || '').replace(/\D/g, '').slice(-4);
        const successData: any = {
          ...response,
          field_payment_id: response.payment_id || this.paymentForm.value.field_payment_id,
          field_reference_number: '',
          field_card_last4: last4,
          field_card_type: 'ACH',
          field_payment_method: 'ach',
          field_surcharge: Number(response.surcharge) > 0 ? Number(response.surcharge).toFixed(2) : '',
          field_paybright_reference_number: response.referenceNumber || response.reference_number || '',
        };
        if (response.saved_bank_account) {
          this.loadPaybrightBankAccounts();
        } else if (response.save_failed) {
          this._snackBar.open('The payment went through, but the bank account could not be saved for future use.', 'Close', { duration: 8000 });
        }
        this._snackBar.open('Bank debit submitted. It settles in a few business days; you will be notified if the bank returns it.', 'Close', { duration: 8000 });
        this.resetAchEntry();
        this.handlePaymentSuccess(successData, false);
      },
      error: (error: any) => {
        this.achProcessing = false;
        this.isSubmitting = false;
        const body = error?.error ?? error;
        if (body?.status === 'APPROVED_UNRECORDED') {
          this.handlePaymentError({
            message: body.error || 'The bank debit was submitted but the payment record could not be saved. Please reconcile manually using the reference number.'
          });
          return;
        }
        this.handlePaymentError({ message: body?.error || body?.error_message || error?.message || 'PayBright ACH payment failed' });
      }
    });
  }

  getEnrollmentIds(): any[] {
    const enrollmentArray = this.paymentForm.get('field_enrollment_name').value;
    if (Array.isArray(enrollmentArray)) {
      return enrollmentArray.map(enrollment => enrollment.target_id);
    }
    return [enrollmentArray.target_id];
  }

  // ── Gift certificate redemption ─────────────────────────────────────────
  // A "Gift Certificate" payment type whose sub-panel looks up a code and
  // whose dedicated submit lets the SERVER book the payment: the redemption
  // endpoint validates the live balance under a row lock and writes the
  // payment itself, so the normal Save Payment path must not run for it.

  gcCode: string = '';
  gcLookup: any = null;
  gcLookupPending = false;
  gcRedeeming = false;

  /**
   * The Gift Certificate payment_type term id, resolved from the unfiltered
   * taxonomy by name (PayBright pattern; a per-database id must never be
   * hardcoded, and a studio hiding the method must not break gating).
   */
  get giftCertificatePaymentTypeId(): number | null {
    const term = this._taxonomyService.payment_type_all?.find(
      p => this.normalizeTermName(p.name) === 'gift certificate'
    );
    return term ? Number(term.id) : null;
  }

  /**
   * True when the selected payment type is Gift Certificate. Matches by id or
   * by the selected term's own name, mirroring isPaybrightTerminalSelected, so
   * a rename cannot silently fall back to the plain Save Payment button (which
   * would record a payment without debiting any certificate).
   */
  get isGiftCertificateSelected(): boolean {
    const selected = this.paymentForm?.get('field_payment_type')?.value;
    if (selected == null) {
      return false;
    }
    if (this.giftCertificatePaymentTypeId != null && Number(selected) === this.giftCertificatePaymentTypeId) {
      return true;
    }
    const selectedTerm = this._taxonomyService.payment_type_all?.find(p => Number(p.id) === Number(selected));
    return !!selectedTerm && this.normalizeTermName(selectedTerm.name) === 'gift certificate';
  }

  /** Looks up the entered code and shows its balance before anything is charged. */
  lookupGiftCertificate() {
    const code = (this.gcCode || '').trim().toUpperCase();
    if (!code) {
      this._snackBar.open('Enter a gift certificate code.', 'Close', { duration: 3000 });
      return;
    }

    this.gcLookupPending = true;
    this.gcLookup = null;

    this._drupalRESTService.httpGET(`/api_rest/v1/getGiftCertificateEntityFromCode?code=${encodeURIComponent(code)}`)
      .subscribe(
        (response: any) => {
          this.gcLookupPending = false;
          if (!response || response.error) {
            this.gcLookup = { error: 'Gift certificate not found.' };
            return;
          }
          this.gcLookup = response;

          // Default the amount to what the certificate can actually cover.
          const remaining = Number(response.remaining_balance) || 0;
          const gross = Number(this.paymentForm.get('field_gross_tuition')?.value) || 0;
          if (response.redeemable && (gross <= 0 || gross > remaining)) {
            this.paymentForm.get('field_total_payment_amount')?.setValue(remaining.toFixed(2));
            this.paymentForm.get('field_gross_tuition')?.setValue(remaining.toFixed(2));
          }
        },
        () => {
          this.gcLookupPending = false;
          this.gcLookup = { error: 'Could not look up the gift certificate.' };
        }
      );
  }

  /**
   * Whether the redeem button may be pressed: a redeemable lookup, a positive
   * amount within the certificate's balance, and exactly one enrollment (the
   * redemption endpoint books against a single enrollment).
   */
  canRedeemGiftCertificate(): boolean {
    if (!this.gcLookup || this.gcLookup.error || !this.gcLookup.redeemable) {
      return false;
    }
    const gross = Number(this.paymentForm.get('field_gross_tuition')?.value) || 0;
    if (gross <= 0 || gross > (Number(this.gcLookup.remaining_balance) || 0)) {
      return false;
    }
    return this.getEnrollmentIds().filter(id => !!id).length === 1;
  }

  /** Redeems the looked-up certificate against the selected enrollment. */
  redeemGiftCertificatePayment() {
    const enrollmentIds = this.getEnrollmentIds().filter(id => !!id);
    const amount = Number(this.paymentForm.get('field_gross_tuition')?.value) || 0;

    this.gcRedeeming = true;
    this.isSubmitting = true;
    this.displayProgressSpinner(true);

    this._drupalRESTService.httpPATCH('/api_rest/v1/redeemGiftCertificate', {
      code: (this.gcCode || '').trim().toUpperCase(),
      amount: amount,
      enrollment_id: enrollmentIds[0],
    }).subscribe(
      (response: any) => {
        const body = response?.body ?? response;
        this.gcRedeeming = false;

        if (!body || body.status !== 'success') {
          this.isSubmitting = false;
          this.displayProgressSpinner(false);
          this._snackBar.open(body?.message || 'Could not redeem this gift certificate.', 'Close', { duration: 6000 });
          return;
        }

        this._snackBar.open(
          `Redeemed ${body.redeemed_amount}. Certificate balance: ${body.remaining_balance}.`,
          'Close', { duration: 6000 }
        );

        // The server booked the payment; surface it on the receipt.
        this.handlePaymentSuccess({
          id: body.payment_id,
          field_payment_id: body.payment_number,
          field_reference_number: body.code,
        }, false);
      },
      (error) => {
        this.gcRedeeming = false;
        this.isSubmitting = false;
        this.displayProgressSpinner(false);
        LoggerService.error('Gift certificate redemption failed:', error);
        this._snackBar.open('Gift certificate redemption failed.', 'Close', { duration: 6000 });
      }
    );
  }

  saveCard() {
    const endpoint = `/api/square/terminal/save-card/${this.data.studioId}`;
    const body = {
      studentAccountId: this.data.studentAccountId,
      shouldSaveCard: true
    };

    this._drupalRESTService.httpPOST(endpoint, body).subscribe(
      (response: any) => {
        if (response.success) {
          this._dialogRef.close({
            status: 'SUCCESS',
            action: response.action
          });
        } else {
          this._dialogRef.close({
            status: 'FAILURE',
            error: response.error || 'Failed to save card'
          });
        }
      },
      error => {
        this._dialogRef.close({
          status: 'FAILURE',
          error: error?.error?.error || error?.message || 'Failed to save card'
        });
      }
    );
  }

  openSquareTerminalSaveCard() {
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);

    // Basic validation
    if (!studentId) {
      this._snackBar.open('Please select a student first.', 'Close', { duration: 5000 });
      return;
    }

    const dialogData = {
      studentAccountId: studentId,
      studioId: this._authService.selectedStudio?.id,
      shouldSaveCard: true,
      idempotencyKey: uuidv4(),
    };

    LoggerService.log('Opening Square Terminal Save Card dialog with data:', dialogData);

    const dialogRef = this._dialogService.openDialog(
      SquareTerminalSaveCardComponent,
      'fullScreenCreditCardInput',
      dialogData
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.status === 'SUCCESS') {
          this.onSquareTerminalSaveCardComplete(result);
          // Refresh the saved cards list
          this.loadSquareSavedCards();
        } else if (result.status === 'FAILURE') {
          this.onSquareTerminalSaveCardError(result.error);
        } else if (result.status === 'CANCELED') {
          this._snackBar.open('Card saving was canceled', 'Close', { duration: 5000 });
        }
      }
    });
  }

  onSquareTerminalSaveCardComplete(event: any) {
    LoggerService.log('Square Terminal card saving completed:', event);
    this._snackBar.open('Card saved successfully', 'Close', { duration: 5000 });
  }

  onSquareTerminalSaveCardError(error: any) {
    LoggerService.error('Square Terminal card saving error:', error);
    this._snackBar.open('Failed to save card: ' + (error.message || 'Unknown error'), 'Close', { duration: 5000 });
  }

  loadSquareSavedCards() {
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);
    const studioId = this._authService.selectedStudio?.id;

    if (!studentId || !studioId) {
      LoggerService.error('Student ID and Studio ID are required');
      return;
    }

    const endpoint = `/api/square/terminal/saved-cards/${studioId}`;
    const params = [
      { parameter: "field_student_name", value: studentId }
    ];

    this._drupalRESTService.httpGET(endpoint, params).subscribe({
      next: (response: any) => {
        LoggerService.log('Saved cards loaded:', response);
        this.square_payment_methods = response.cards || [];
      },
      error: (error) => {
        LoggerService.error('Error loading saved cards:', error);
        this._snackBar.open('Failed to load saved cards', 'Close', { duration: 5000 });
      }
    });
  }

  isSquarePaymentMethodSelected(): boolean {
    const paymentType = this.paymentForm.get('field_payment_type')?.value;
    const selectedPaymentMethod = this.paymentForm.get('field_payments_use_card')?.value;

    if (paymentType !== '1422') {
      return true;
    }

    // Check if either terminal_payment or a saved card ID is selected
    return selectedPaymentMethod === 'terminal_payment' ||
           (selectedPaymentMethod && this.square_payment_methods?.some(card => card.id === selectedPaymentMethod));
  }

  /**
   * Delete a Square saved payment method
   */
  delete_square_payment_method(cardId: string) {
    const studentId = this.regexStudentName(this.paymentForm.get('field_student_name')?.value);
    const studioId = this._authService.selectedStudio?.id;

    if (!studentId || !studioId) {
      this._snackBar.open('Missing required information to delete card', 'Close', { duration: 5000 });
      return;
    }

    // Confirm deletion
    const confirmDelete = confirm('Are you sure you want to delete this saved payment method?');
    if (!confirmDelete) {
      return;
    }

    const endpoint = `/api/square/terminal/delete-card/${studioId}/${cardId}?field_student_name=${studentId}`;

    this._drupalRESTService.httpDELETE(endpoint).subscribe({
      next: (response: any) => {
        LoggerService.log('Delete card response:', response);

        try {
          if (response && response.success) {
            this._snackBar.open('Payment method deleted successfully', 'Close', { duration: 5000 });

            // Remove the deleted card from the local array
            if (this.square_payment_methods) {
              this.square_payment_methods = this.square_payment_methods.filter(card => card.id !== cardId);
            }

            // Reset the selected payment method if it was the deleted card
            const currentSelection = this.paymentForm.get('field_payments_use_card')?.value;
            if (currentSelection === cardId) {
              this.paymentForm.get('field_payments_use_card')?.setValue('terminal_payment');
            }
          } else {
            LoggerService.warn('Unexpected response format:', response);
            this._snackBar.open('Failed to delete payment method: ' + (response?.error || 'Unexpected response format'), 'Close', { duration: 8000 });
          }
        } catch (e) {
          LoggerService.error('Error processing delete response:', e);
          this._snackBar.open('Error processing delete response: ' + e.message, 'Close', { duration: 8000 });
        }
      },
      error: (error) => {
        LoggerService.error('HTTP Error deleting Square payment method:', error);
        LoggerService.error('Error status:', error.status);
        LoggerService.error('Error body:', error.error);

        let errorMessage = 'Failed to delete payment method';

        if (error.status === 200 && error.error && typeof error.error === 'string') {
          // Sometimes a 200 response gets treated as an error if it's not valid JSON
          try {
            const parsedResponse = JSON.parse(error.error);
            if (parsedResponse.success) {
              // This was actually successful, handle it as success
              this._snackBar.open('Payment method deleted successfully', 'Close', { duration: 5000 });
              if (this.square_payment_methods) {
                this.square_payment_methods = this.square_payment_methods.filter(card => card.id !== cardId);
              }
              const currentSelection = this.paymentForm.get('field_payments_use_card')?.value;
              if (currentSelection === cardId) {
                this.paymentForm.get('field_payments_use_card')?.setValue('terminal_payment');
              }
              return;
            }
          } catch (parseError) {
            LoggerService.error('Could not parse error response as JSON:', parseError);
          }
        }

        if (error?.error?.error) {
          errorMessage = error.error.error;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this._snackBar.open('Error: ' + errorMessage, 'Close', { duration: 8000 });
      }
    });
  }

  /**
   * Getter that returns filtered enrollments excluding dropped/refunded ones
   */
  get availableEnrollmentsForUser(): any[] {
    if (!this.enrollmentsForUser) {
      return [];
    }

    return this.enrollmentsForUser.filter(enrollment => {
      // Check if enrollment is dropped
      const droppedDate = enrollment.field_dropped_date?.[0]?.value;
      const isDropped = droppedDate && droppedDate !== "" && droppedDate !== null && droppedDate !== undefined;

      if (isDropped) {
        LoggerService.log('FILTERED OUT - Dropped enrollment with date:', droppedDate, 'Enrollment ID:', enrollment.id?.[0]?.value);
        return false;
      }

      return true;
    });
  }

  /**
   * Override ngAfterContentInit to prevent calling getEntity when nested
   * When nested in enrollment form, this component receives data via @Input properties
   */
  override ngAfterContentInit() {
    if (this.isNested) {
      // Don't call getEntity when nested
      return;
    }
    // When not nested, call parent implementation
    super.ngAfterContentInit();
  }

}
